import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { GripVertical, X } from 'lucide-react'

interface Point { x: number; y: number }

interface RulerPopupProps {
  anchor: Point
  side: 'left' | 'right'
  color: string
  label: string
  metric: 'price' | 'oi'
  measuring: boolean
  onClear: () => void
  children: ReactNode
}

/** Keep a manually positioned measurement still while chart data and scales update. */
export function RulerPopup({ anchor, side, color, label, metric, measuring, onClear, children }: RulerPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ pointerId: number; origin: Point; position: Point } | null>(null)
  const [manualPosition, setManualPosition] = useState<Point | null>(null)
  const [dragging, setDragging] = useState(false)
  const [size, setSize] = useState({ width: 354, height: 470 })
  const [viewport, setViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }))

  useLayoutEffect(() => {
    const popup = popupRef.current
    if (!popup) return
    const measure = () => {
      const { width, height } = popup.getBoundingClientRect()
      setSize((previous) => previous.width === width && previous.height === height ? previous : { width, height })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(popup)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const resize = () => setViewport({ width: window.innerWidth, height: window.innerHeight })
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [])

  useEffect(() => {
    if (measuring) setManualPosition(null)
  }, [measuring])

  const clamp = (position: Point): Point => ({
    x: Math.max(8, Math.min(position.x, viewport.width - size.width - 8)),
    y: Math.max(8, Math.min(position.y, viewport.height - size.height - 8)),
  })
  const position = clamp(manualPosition ?? {
    x: side === 'left' ? anchor.x - size.width - 18 : anchor.x + 18,
    y: anchor.y + 18,
  })

  const startDrag = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0 || measuring) return
    event.preventDefault()
    event.currentTarget.focus({ preventScroll: true })
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = {
      pointerId: event.pointerId,
      origin: { x: event.clientX, y: event.clientY },
      position,
    }
    setManualPosition(position)
    setDragging(true)
  }

  const moveDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    setManualPosition(clamp({
      x: drag.position.x + event.clientX - drag.origin.x,
      y: drag.position.y + event.clientY - drag.origin.y,
    }))
  }

  const endDrag = (event: PointerEvent<HTMLButtonElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return
    dragRef.current = null
    setDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }

  return <div
    ref={popupRef} role="dialog" aria-label={`${metric === 'price' ? 'Price' : 'OI'} ruler measurement`} data-testid="screener-ruler-popup"
    className="fixed z-[100] flex flex-col overflow-hidden rounded-xl border bg-[#0b1220]/95 text-xs text-slate-200 shadow-2xl backdrop-blur"
    style={{ left: position.x, top: position.y, width: 'min(354px, calc(100vw - 16px))', maxHeight: 'calc(100vh - 16px)', borderColor: `${color}80`, pointerEvents: measuring ? 'none' : 'auto' }}
  >
    <div className="flex shrink-0 items-center border-b border-white/10 pr-2" style={{ backgroundColor: `${color}20` }}>
      <button
        type="button" aria-label="Move ruler data panel" title="Drag to move · Arrow keys to move · Double-click to reset"
        className="flex min-w-0 flex-1 touch-none select-none items-center gap-1.5 px-3 py-3 text-left outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-slate-400"
        style={{ cursor: dragging ? 'grabbing' : 'grab' }}
        onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag}
        onPointerCancel={endDrag} onLostPointerCapture={endDrag}
        onDoubleClick={() => setManualPosition(null)}
        onKeyDown={(event) => {
          const delta: Record<string, Point> = { ArrowLeft: { x: -1, y: 0 }, ArrowRight: { x: 1, y: 0 }, ArrowUp: { x: 0, y: -1 }, ArrowDown: { x: 0, y: 1 } }
          const direction = delta[event.key]
          if (!direction || measuring) return
          event.preventDefault()
          const step = event.shiftKey ? 40 : 10
          setManualPosition(clamp({ x: position.x + direction.x * step, y: position.y + direction.y * step }))
        }}
      >
        <GripVertical className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
        <span className="font-semibold uppercase tracking-wide">{label} · {metric === 'price' ? 'Price' : 'OI'} ruler</span>
      </button>
      <button type="button" onClick={onClear} aria-label="Clear ruler" className="rounded p-1 text-slate-400 hover:bg-white/10 hover:text-white"><X className="h-3.5 w-3.5" /></button>
    </div>
    <div className="min-h-0 overflow-y-auto">{children}</div>
    <div className="shrink-0 border-t border-white/10 px-3 py-2 text-[10px] text-slate-500">{measuring ? 'Click to finish · Esc to cancel' : 'Drag header to move · Shift-click to measure · Esc to clear'}</div>
  </div>
}
